import { useCallback, useMemo, useRef, useState } from 'react'
import {
  FlatList,
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'

import { Icon, symbolName } from '../../../components/ui/Icon'
import { colors } from '../../../theme'
import { label2, label3 } from '../../../theme/typography'

const ITEM_HEIGHT = 48
const DROPDOWN_MAX_HEIGHT = 320
const ITEM_VERTICAL_GAP = 4
const LIST_ITEM_LAYOUT_HEIGHT = ITEM_HEIGHT + ITEM_VERTICAL_GAP
const DROPDOWN_OFFSET = 4
const VIEWPORT_MARGIN = 16
const DROPDOWN_WIDTH = 180
const DROPDOWN_CONTENT_PADDING = 12

function WeekPickerRow({
  currentWeek,
  onSelect,
  viewedWeek,
  week,
}: {
  currentWeek: number
  onSelect: (week: number) => void
  viewedWeek: number
  week: number
}) {
  const { t } = useTranslation('dashboard')
  const isSelected = viewedWeek === week
  const isCurrent = currentWeek === week

  return (
    <Pressable
      onPress={() => onSelect(week)}
      className={`flex-row items-center justify-between rounded-lg px-3 ${isSelected ? 'bg-primary' : isCurrent ? 'bg-primary/5' : ''}`}
      style={weekRowStyle}
    >
      <Text className={isSelected ? 'text-white' : 'text-ink'} style={label2}>
        {t('week_n', { week })}
      </Text>
      {isCurrent && !isSelected && (
        <View className="rounded-full bg-primary/15 px-1.5 py-0.5">
          <Text style={label3} className="text-primary">
            {t('week_picker_go_to_today')}
          </Text>
        </View>
      )}
      {isSelected && (
        <Icon name={symbolName('checkmark', 'check')} size={16} color="white" />
      )}
    </Pressable>
  )
}

interface WeekPickerProps {
  viewedWeek: number
  currentWeek: number
  maxWeek: number
  onWeekChange: (week: number) => void
}

export function WeekPicker({
  viewedWeek,
  currentWeek,
  maxWeek,
  onWeekChange,
}: WeekPickerProps) {
  const { t } = useTranslation('dashboard')
  const { height: windowHeight } = useWindowDimensions()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<View>(null)
  const listRef = useRef<FlatList<number>>(null)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 16,
    maxHeight: DROPDOWN_MAX_HEIGHT,
  })

  const isViewingCurrentWeek = viewedWeek === currentWeek
  const weeks = Array.from({ length: maxWeek }, (_, i) => i + 1)
  const selectedWeekIndex = useMemo(() => {
    if (weeks.length === 0) {
      return 0
    }

    return Math.min(Math.max(viewedWeek - 1, 0), weeks.length - 1)
  }, [viewedWeek, weeks.length])
  const dropdownContentHeight = Math.min(
    DROPDOWN_MAX_HEIGHT,
    weeks.length * LIST_ITEM_LAYOUT_HEIGHT + DROPDOWN_CONTENT_PADDING,
  )
  const handleSelect = useCallback(
    (week: number) => {
      onWeekChange(week)
      setIsOpen(false)
    },
    [onWeekChange],
  )
  const renderItem = useCallback(
    ({ item: week }: { item: number }) => (
      <WeekPickerRow
        currentWeek={currentWeek}
        onSelect={handleSelect}
        viewedWeek={viewedWeek}
        week={week}
      />
    ),
    [currentWeek, handleSelect, viewedWeek],
  )

  const handleOpen = () => {
    triggerRef.current?.measureInWindow((_x, y, _width, height) => {
      const dropdownTopBelowTrigger = y + height + DROPDOWN_OFFSET
      const spaceBelow =
        windowHeight - dropdownTopBelowTrigger - VIEWPORT_MARGIN
      const spaceAbove = y - DROPDOWN_OFFSET - VIEWPORT_MARGIN
      const shouldOpenAbove =
        spaceBelow < dropdownContentHeight && spaceAbove > spaceBelow
      const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow
      const maxHeight = Math.max(
        Math.min(dropdownContentHeight, availableHeight),
        LIST_ITEM_LAYOUT_HEIGHT + DROPDOWN_CONTENT_PADDING,
      )
      const top = shouldOpenAbove
        ? Math.max(VIEWPORT_MARGIN, y - DROPDOWN_OFFSET - maxHeight)
        : Math.max(VIEWPORT_MARGIN, dropdownTopBelowTrigger)

      setDropdownPosition({
        top,
        right: 16,
        maxHeight,
      })
      setIsOpen(true)
    })
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <View className="flex-row items-center gap-1.5">
      {!isViewingCurrentWeek && (
        <Pressable
          onPress={() => onWeekChange(currentWeek)}
          hitSlop={4}
          className="flex-row items-center gap-1 rounded-full bg-primary px-2.5"
          style={{ height: 28 }}
        >
          <Icon
            name={symbolName('arrow.uturn.backward', 'undo')}
            size={12}
            color="white"
          />
          <Text style={label3} className="text-white">
            {t('week_picker_go_to_today')}
          </Text>
        </Pressable>
      )}

      <Pressable
        ref={triggerRef}
        onPress={handleOpen}
        hitSlop={4}
        className={`flex-row items-center gap-0.5 rounded-full px-3 ${
          isViewingCurrentWeek ? 'bg-fill' : 'bg-primary/10'
        }`}
        style={{ height: 32 }}
      >
        <Text
          className={isViewingCurrentWeek ? 'text-ink' : 'text-primary'}
          style={label2}
        >
          {t('week_n', { week: viewedWeek })}
        </Text>
        <Icon
          name={symbolName('chevron.down', 'expand_more')}
          size={16}
          color={isViewingCurrentWeek ? 'inkSecondary' : colors.primary}
        />
      </Pressable>

      <Modal
        animationType="fade"
        transparent
        visible={isOpen}
        onRequestClose={handleClose}
      >
        <Pressable className="flex-1" onPress={handleClose}>
          <Pressable
            onPress={(pressEvent) => {
              pressEvent.stopPropagation()
            }}
            className="absolute bg-white rounded-xl shadow-lg"
            style={{
              top: dropdownPosition.top,
              right: dropdownPosition.right,
              width: DROPDOWN_WIDTH,
            }}
          >
            <FlatList
              ref={listRef}
              data={weeks}
              keyExtractor={(item) => item.toString()}
              style={{ maxHeight: dropdownPosition.maxHeight }}
              contentContainerStyle={{ padding: 6 }}
              showsVerticalScrollIndicator
              persistentScrollbar
              initialScrollIndex={selectedWeekIndex}
              getItemLayout={(_data, index) => ({
                length: LIST_ITEM_LAYOUT_HEIGHT,
                offset: LIST_ITEM_LAYOUT_HEIGHT * index,
                index,
              })}
              initialNumToRender={15}
              onScrollToIndexFailed={(info) => {
                setTimeout(() => {
                  listRef.current?.scrollToIndex({
                    index: Math.min(
                      Math.max(info.index, 0),
                      weeks.length > 0 ? weeks.length - 1 : 0,
                    ),
                    animated: false,
                    viewPosition: 0.5,
                  })
                }, 500)
              }}
              renderItem={renderItem}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  )
}

const weekRowStyle = {
  height: ITEM_HEIGHT,
  marginBottom: ITEM_VERTICAL_GAP,
} as const
